import { DeleteOutlined, EditOutlined, PlusOutlined, ShopOutlined } from '@ant-design/icons';
import { App, Button, Card, Descriptions, DescriptionsProps, Empty, Popconfirm } from 'antd';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { btlApplicationFormApi } from '@api/btlApplicationForm/btlApplicationFormApi';
import { BtlCompany } from '../../../../api/btlApplicationForm/types';
import { PageURL } from '../../../../router/pageURL';
import { TypedFormatMessageFn } from '../../../../types/intl';
import { BtlApplicationFormPageEnum } from '../../types';
import { updateUrl } from '../Applicants';

export const CompanyDescription = ({ company }: { company: BtlCompany }) => {
  const { formatMessage }: { formatMessage: TypedFormatMessageFn } = useIntl();
  const hasCompany = !!company;
  const companyDetails: DescriptionsProps['items'] = hasCompany
    ? [
        {
          label: formatMessage({ id: 'btlApplicationForm.APPLICANTS_COMPANY.address.label' }),
          children: (
            <>
              {Object.entries(company.registeredAddress)
                .filter(([key, value]) => key !== 'id' && value)
                .map(([key, value]) => (
                  <div key={key}>{`${value}`}</div>
                ))}
            </>
          ),
        },
      ]
    : [];
  return (
    <Descriptions
      styles={{ label: { textAlign: 'end', alignContent: 'start' } }}
      items={companyDetails}
      bordered={true}
      size={'middle'}
    />
  );
};

export const CompanyCard = ({
  companies,
  applicationId,
  refetch,
  isEditable,
}: {
  companies?: BtlCompany[];
  applicationId: string;
  refetch: () => void;
  isEditable: boolean;
}) => {
  const { message } = App.useApp();
  const { formatMessage }: { formatMessage: TypedFormatMessageFn } = useIntl();
  const navigate = useNavigate();
  const hasCompany = companies && companies.length !== 0;
  const companyData = companies?.[0] as BtlCompany;

  return (
    <Card
      title={
        companyData ? (
          <>
            <span className={'ant-descriptions-title inline'}>
              <ShopOutlined /> {companyData?.companyName}
            </span>{' '}
            <span className={'font-normal'}>({companyData?.companyNumber})</span>
          </>
        ) : null
      }
      actions={[
        <Button
          key='actionCompany'
          className={'w-full'}
          type={'link'}
          onClick={() =>
            updateUrl(PageURL.BTL_APPLICATION_EDIT, BtlApplicationFormPageEnum.APPLICANTS_COMPANY, navigate, {
              applicationId,
              companyId: hasCompany ? companyData.id : '',
            })
          }
          icon={hasCompany ? <EditOutlined /> : <PlusOutlined />}
        >
          {formatMessage({
            id:
              hasCompany && isEditable
                ? 'btlApplicationForm.APPLICANTS_COMPANY.editCompany.label'
                : hasCompany
                ? 'btlApplicationForm.APPLICANTS_COMPANY.viewCompany.label'
                : 'btlApplicationForm.APPLICANTS_COMPANY.addCompany.label',
          })}
        </Button>,
        hasCompany && isEditable ? (
          <Popconfirm
            key='removeCompany'
            title={formatMessage({ id: 'btlApplicationForm.APPLICANTS_COMPANY.removeCompany.label' })}
            description={formatMessage({ id: 'btlApplicationForm.APPLICANTS_COMPANY.removeCompany.prompt' })}
            onConfirm={async () => {
              try {
                await btlApplicationFormApi.deleteCompany(applicationId, companyData.id);
                refetch();
                message.success(formatMessage({ id: 'btlApplicationForm.APPLICANTS_COMPANY.removeCompany.success' }));
              } catch (e: any) {
                message.error(e.message);
              }
            }}
            okText={formatMessage({ id: 'common.yes' })}
            cancelText={formatMessage({ id: 'common.no' })}
          >
            <Button type={'link'} danger icon={<DeleteOutlined />}>
              {formatMessage({ id: 'btlApplicationForm.APPLICANTS_COMPANY.removeCompany.label' })}
            </Button>
          </Popconfirm>
        ) : null,
      ].filter(val => val)}
    >
      {hasCompany ? (
        <CompanyDescription company={companyData} />
      ) : (
        <Empty description={formatMessage({ id: 'btlApplicationForm.APPLICANTS_COMPANY.noCompany.label' })} />
      )}
    </Card>
  );
};
